import type { SignalDay, SignalQuality } from '../types/signalStrength'
import { utcDayKey } from './dailyDelivery'

/**
 * Per-day bars for the device Signal strength chart.
 *
 * Every UTC day in the window gets a slot, oldest first, so the chart lines up
 * day-for-day with the missed-messages strip beside it. A day with no samples
 * is a slot with no data, not a missing slot - signal capture only began on
 * 29 Sep, and a window that silently started then would hide that.
 *
 * WHY THERE IS NO HEIGHT
 *
 * Height used to scale with margin, which double-encoded the same value the
 * colour already carries - and the height channel collapsed exactly where the
 * chart matters most. A device at 2 dB drew every bar at the 8% floor, so a
 * critical install rendered as a row of slivers and read as "nothing here"
 * rather than "this is the bad one". Bars are now uniform and colour alone
 * carries the rating, which the legend already spells out.
 *
 * Days with NO data stay short on purpose. That difference is structural, not
 * chromatic, so "we heard nothing" never has to be told apart from "we heard
 * something terrible" by hue alone.
 */

export interface SignalBar {
  day: string
  /** That day's figures, or null when nothing was heard. */
  row: SignalDay | null
  quality: SignalQuality
}

const DAY_MS = 86_400_000

/** The `days` UTC day keys ending today, oldest first. */
export function windowDays(days: number, now: Date): string[] {
  const todayStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  return Array.from({ length: days }, (_, i) => utcDayKey(new Date(todayStart - (days - 1 - i) * DAY_MS)))
}

export function signalBars(daily: SignalDay[], days: number, now: Date): SignalBar[] {
  const byDay = new Map(daily.map((d) => [d.day, d]))
  return windowDays(days, now).map((day) => {
    const row = byDay.get(day) ?? null
    return { day, row, quality: row ? row.quality : 'unknown' }
  })
}

/** First day in the window that has any signal, or null if none does. */
export function firstDataDay(bars: SignalBar[]): string | null {
  return bars.find((b) => b.row !== null)?.day ?? null
}
